import { useState, useCallback, useEffect } from 'react';
import { Category } from '@/types';
import { toast } from 'sonner';

const STORAGE_KEY = 'glmt-categories';
const EXPANDED_STORAGE_KEY = 'glmt-expanded';

export const useCategories = () => {
  const [categories, setCategories] = useState<Category[]>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  });

  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const stored = localStorage.getItem(EXPANDED_STORAGE_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(categories));
  }, [categories]);

  useEffect(() => {
    localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify(Array.from(expanded)));
  }, [expanded]);

  const handleToggle = useCallback((categoryId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  }, []);

  const handleAddCategory = useCallback((name: string, parentId: string | null, type: 'Afdracht' | 'Geen afdracht') => {
    console.log('Adding new category:', name, 'with parent:', parentId, 'type:', type);
    
    const newCategory: Category = {
      id: `cat-${Date.now()}`,
      name,
      level: 0,
      sortOrder: categories.length + 1,
      children: [],
      accounts: [],
      mappingRules: [],
      type,
      parentId
    };

    setCategories((prevCategories) => {
      if (!parentId) {
        console.log('Adding top-level category:', newCategory);
        return [...prevCategories, newCategory];
      }

      const addToParent = (cats: Category[]): Category[] => {
        return cats.map((cat) => {
          if (cat.id === parentId) {
            return {
              ...cat,
              children: [...cat.children, { ...newCategory, level: cat.level + 1 }],
            };
          }
          return {
            ...cat,
            children: addToParent(cat.children),
          };
        });
      };

      const updatedCategories = addToParent(prevCategories);
      console.log('Updated categories:', updatedCategories);
      return updatedCategories;
    });
  }, [categories.length]);

  const handleEditCategory = useCallback((
    categoryId: string, 
    newName: string, 
    newParentId: string | null, 
    newType: 'Afdracht' | 'Geen afdracht'
  ) => {
    setCategories(prevCategories => {
      const updateCategory = (cats: Category[]): Category[] => {
        return cats.map(cat => {
          if (cat.id === categoryId) {
            return { ...cat, name: newName, type: newType };
          }
          return {
            ...cat,
            children: updateCategory(cat.children),
          };
        });
      };
      return updateCategory(prevCategories);
    });
  }, []);

  const handleReorderCategories = useCallback((newCategories: Category[]) => {
    console.log('Reordering categories:', newCategories);
    setCategories(newCategories);
  }, []);

  return {
    categories,
    expanded,
    handleToggle,
    handleAddCategory,
    handleEditCategory,
    handleReorderCategories,
    setCategories,
  };
};

